import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Container, Typography } from "@material-ui/core";
import R from "ramda";
import useVisibilitySensor from "@rooks/use-visibility-sensor";
import SwiperCore, { Navigation, Autoplay } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import { useHover } from "hooks";
import StarringYouClip from "components/StarringYouClip";
import Placeholder from "components/Placeholder";
import StarringYouClipPresenter from "presenters/StarringYouClipPresenter";

import useStyles from "./useStyles";

import "swiper/swiper-bundle.min.css";

const StarringYouClipsCarousel = ({ items, loading = false }) => {
  const classes = useStyles();
  const sliderContainerRef = useRef(null);
  const swiperRef = useRef(null);
  const [ref, isHovered] = useHover();

  const { isVisible } = useVisibilitySensor(sliderContainerRef, {
    intervalCheck: false,
    scrollCheck: true,
    resizeCheck: true,
    partialVisibility: "top",
    minTopValue: R.pathOr(0, ["current", "offsetHeight"], swiperRef) / 2,
  });

  useEffect(() => {
    if (isVisible && !isHovered) {
      swiperRef.current.swiper.autoplay.start();
    } else {
      swiperRef.current.swiper.autoplay.stop();
    }
  }, [isVisible, isHovered]);

  const renderSlide = (isActive, item) => (
    <div className={classes.slide} key={StarringYouClipPresenter.id(item)}>
      <StarringYouClip item={item} playing={isVisible && isActive} />
      <div className={classes.overlay} />
      <img src={item.titleImage} alt={item.name} className={classes.swiperCarouselImageName} />
      <span className={classes.swiperCarouselDuration}>{dayjs.duration(item.duration, "seconds").format("m:ss")}</span>
    </div>
  );

  const renderContent = () => {
    if (loading)
      return R.range(0, 2).map((index) => (
        <div className={classes.slide} key={`StarringYouClipsCarousel-placeholder-${index}`}>
          <StarringYouClip loading />
        </div>
      ));
    return (
      <>
        {items &&
          items.filter(StarringYouClipPresenter.animatedThumb).map((item) => {
            dayjs.extend(duration);

            return (
              <SwiperSlide key={StarringYouClipPresenter.id(item)}>
                {({ isActive }) => renderSlide(isActive, item)}
              </SwiperSlide>
            );
          })}
      </>
    );
  };

  const renderTitle = () => {
    if (loading)
      return (
        <Typography variant="h2" className={classes.title}>
          <Placeholder variant="text" />
        </Typography>
      );

    if (R.isEmpty(items)) return null;

    return (
      <Typography variant="h2" className={classes.title}>
        NEW SHORTIES! <span>Shorter Videos to Share Everywhere</span>
      </Typography>
    );
  };

  SwiperCore.use([Navigation, Autoplay]);

  return (
    <div className={classes.root}>
      <Container ref={ref}>
        {renderTitle()}

        <div ref={sliderContainerRef} className={classes.sliderWrapper}>
          <Swiper
            ref={swiperRef}
            slidesPerView={1}
            loop
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            navigation
            breakpoints={{
              0: {
                slidesPerView: 1.1,
                spaceBetween: 16,
              },
              650: {
                slidesPerView: 1.6,
                spaceBetween: 16,
              },
            }}
          >
            {renderContent()}
          </Swiper>
        </div>
      </Container>
    </div>
  );
};

StarringYouClipsCarousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      attributes: PropTypes.shape({}),
    }).isRequired,
  ),
  loading: PropTypes.bool,
};

export default StarringYouClipsCarousel;
